/**
 * P2 design demo — toggle between trainer dashboard view and live studio
 * console view. Studio uses tabbed main area (no scroll), persistent
 * right rail.
 */
import { useState } from "react";

export default function DesignDemo() {
  const [view, setView] = useState<"dashboard" | "studio">("studio");
  return (
    <>
      <DemoSwitch view={view} setView={setView} />
      {view === "dashboard" ? <DashboardDemo /> : <StudioDemo />}
    </>
  );
}

function DemoSwitch({ view, setView }: { view: string; setView: (v: any) => void }) {
  return (
    <div style={{
      position: "fixed", bottom: 12, left: 12, zIndex: 200,
      display: "inline-flex", background: "var(--paper-warm)",
      border: "1px solid var(--ink)", borderRadius: 4, overflow: "hidden",
      boxShadow: "3px 3px 0 var(--ink)", fontSize: 10,
      fontFamily: "var(--mono)",
      opacity: 0.85,
    }}>
      <span style={{
        padding: "6px 8px",
        background: "var(--ink)", color: "var(--paper)",
        letterSpacing: "0.1em", textTransform: "uppercase",
      }}>demo</span>
      {(["dashboard", "studio"] as const).map((v) => (
        <button
          key={v}
          onClick={() => setView(v)}
          style={{
            padding: "6px 12px", border: "none", cursor: "pointer",
            background: view === v ? "var(--rust)" : "transparent",
            color: view === v ? "var(--paper)" : "var(--ink)",
            textTransform: "uppercase", letterSpacing: "0.08em",
          }}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   STUDIO — fixed-viewport tabbed console
   ═══════════════════════════════════════════════════════════════════ */

const TABS = [
  { id: "video",    num: "01", label: "影像 ＆ Yui", en: "Video"   },
  { id: "signals",  num: "02", label: "訊號",        en: "Signals" },
  { id: "log",      num: "03", label: "紀錄",        en: "Log"     },
] as const;

type TabId = typeof TABS[number]["id"];

function StudioDemo() {
  const [tab, setTab] = useState<TabId>("video");

  return (
    <div style={{
      display: "grid",
      gridTemplateRows: "auto auto 1fr",
      gridTemplateColumns: "1fr 420px",
      height: "100vh",
      overflow: "hidden",
    }}>
      {/* ── compact mast ── */}
      <header className="mast" style={{
        gridColumn: "1 / 3",
        padding: "10px var(--s-5)",
        borderBottom: "1px solid var(--ink)",
      }}>
        <div className="row" style={{ gap: "var(--s-5)" }}>
          <div className="mast__brand" style={{ fontSize: "var(--t-base)" }}>
            <span>CogniHome</span>
            <span className="slash">/</span>
            <span style={{ color: "var(--ink-soft)" }}>Studio</span>
          </div>
          <span className="chip chip--olive">live · 11m 47s</span>
          <span className="serial">SID 01KR2DX9B0PPYFA</span>
        </div>
        <nav className="mast__nav">
          <span className="muted" style={{ fontSize: "var(--t-sm)" }}>個案 <span className="italic-display" style={{ color: "var(--ink)" }}>張小明</span></span>
          <span style={{ color: "var(--ink-ghost)" }}>·</span>
          <a href="#" style={{ color: "var(--rust)" }}>結束 session</a>
        </nav>
      </header>

      {/* ── tab strip ── */}
      <nav style={{
        gridColumn: "1 / 2",
        display: "flex",
        borderBottom: "1px solid var(--hairline)",
        padding: "0 var(--s-5)",
        background: "var(--paper)",
      }}>
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                background: "transparent",
                border: "none",
                padding: "14px 18px 12px",
                cursor: "pointer",
                position: "relative",
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                color: active ? "var(--ink)" : "var(--ink-soft)",
                transition: "color 160ms",
              }}
            >
              <span className="serial" style={{ color: active ? "var(--rust)" : "var(--ink-faint)" }}>
                {t.num}
              </span>
              <span className="italic-display" style={{
                fontSize: "var(--t-md)",
                fontStyle: "italic",
                fontWeight: active ? 500 : 400,
              }}>
                {t.label}
              </span>
              <span className="muted" style={{
                fontSize: "var(--t-xs)",
                fontFamily: "var(--mono)",
                marginLeft: 4,
              }}>
                {t.en}
              </span>
              {active && (
                <span style={{
                  position: "absolute",
                  bottom: -1, left: 0, right: 0,
                  height: 2, background: "var(--ink)",
                }} />
              )}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <div className="row" style={{ paddingRight: 0, fontSize: "var(--t-xs)" }}>
          <span className="muted" style={{ fontFamily: "var(--mono)" }}>↑↓ ←→ navigate</span>
        </div>
      </nav>

      {/* ── tab content (no scroll, fits remaining viewport) ── */}
      <section style={{
        gridColumn: "1 / 2",
        padding: "var(--s-5)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}>
        {tab === "video"   && <VideoTab />}
        {tab === "signals" && <SignalsTab />}
        {tab === "log"     && <LogTab />}
      </section>

      {/* ── right rail · pure parameters + apps · no scroll ── */}
      <aside style={{
        gridColumn: "2 / 3",
        gridRow: "2 / 4",
        background: "var(--paper-warm)",
        borderLeft: "1px solid var(--hairline)",
        padding: "var(--s-4) var(--s-5)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "var(--s-4)",
      }}>
        <RightRail />
      </aside>
    </div>
  );
}

/* ─── TAB 1: 影像 ＆ Yui — top: 3 cols (cam/screen/reward), bottom: 2 cols (markers/Yui) ─── */
function VideoTab() {
  return (
    <div className="col" style={{ flex: 1, gap: "var(--s-4)", minHeight: 0 }}>
      {/* Top row: 3 equal columns */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "var(--s-4)",
      }}>
        <VideoTile kind="webcam" label="個案攝影機"   caption="home cam · 1280×720" />
        <VideoTile kind="game"   label="個案螢幕鏡像" caption="SoraMynd plane · WebRTC" />
        <RewardPanel />
      </div>

      {/* Bottom row: 50/50 markers + Yui */}
      <div style={{
        flex: 1, minHeight: 0,
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-4)",
      }}>
        {/* 行為觀察 */}
        <div className="surface" style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
            <div>
              <div className="serial">行為觀察 · markers</div>
              <div style={{ fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>手動標記要對比訊號的時刻</div>
            </div>
            <button className="btn btn--small btn--rust">＋ 標記</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {BEHAVIOR_MARKERS.map((m, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "50px 70px 1fr 50px", gap: "var(--s-2)",
                padding: "8px 0", borderBottom: "1px solid var(--hairline-soft)", alignItems: "center",
              }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>{m.t}</span>
                <span className={`chip chip--${m.tone}`}>{m.kind}</span>
                <span style={{ fontSize: "var(--t-xs)", lineHeight: 1.4 }}>{m.note}</span>
                <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--ink-faint)", textAlign: "right" }}>α {m.alpha}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Yui */}
        <div className="surface" style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
            <div>
              <div className="serial">Yui · advisory</div>
              <div style={{ fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>純諮詢 · 不寫參數</div>
            </div>
            <span className="chip chip--petrol">awake</span>
          </div>
          <div className="col" style={{ gap: "var(--s-2)", flex: 1, overflowY: "auto", paddingRight: 4 }}>
            {YUI_MESSAGES.map((m, i) => <YuiBubble key={i} {...m} />)}
          </div>
          <div className="row" style={{ marginTop: "var(--s-3)", gap: "var(--s-2)" }}>
            <input type="text" placeholder="問 Yui …" style={{ fontSize: "var(--t-sm)" }} />
            <button className="btn btn--ghost btn--small">送出</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RewardPanel() {
  return (
    <div style={{
      background: "var(--ink)", color: "var(--paper)",
      padding: "var(--s-4) var(--s-5)",
      borderRadius: "var(--r-md)",
      display: "flex", flexDirection: "column",
      minHeight: 0,
    }}>
      <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
        <div className="serial" style={{ color: "var(--paper-deep)" }}>Now · live feedback</div>
        <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--paper-deep)" }}>
          updated 320ms ago
        </span>
      </div>

      {/* Big reward number with sparkline */}
      <div style={{ marginBottom: "var(--s-4)" }}>
        <div className="serial" style={{ color: "var(--rust)", marginBottom: 4 }}>reward · formula output</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "var(--s-3)", marginBottom: 6 }}>
          <span style={{
            fontFamily: "var(--mono)", fontSize: "var(--t-3xl)", fontWeight: 600,
            color: "var(--rust)", lineHeight: 1, letterSpacing: "-0.02em",
          }}>0.78</span>
          <span style={{ fontSize: "var(--t-sm)", color: "var(--olive)" }}>↗ +0.12 / 60s</span>
        </div>
        <div style={{ height: 36 }}>
          <Sparkline data={generateSeries(60, 0.7)} primary />
        </div>
      </div>

      {/* 3 secondary metrics with sparklines */}
      <div className="col" style={{ gap: "var(--s-3)", flex: 1, minHeight: 0 }}>
        <DarkMetric label="alpha" sub="EEG band-power"  value="12.4" unit="µV²" trend="↗" data={generateSeries(40, 0.5)} />
        <DarkMetric label="θ/β"   sub="EEG ratio"       value="2.1"  unit=""    trend="→" data={generateSeries(40, 0.2)} />
        <DarkMetric label="rmssd" sub="HRV time-domain" value="48"   unit="ms"  trend="↗" data={generateSeries(40, 0.4)} />
      </div>

      {/* Impedance compact strip */}
      <div style={{ marginTop: "var(--s-4)", paddingTop: "var(--s-3)", borderTop: "1px solid #3a342c" }}>
        <div className="row row--between" style={{ marginBottom: 6 }}>
          <span className="serial" style={{ color: "var(--paper-deep)" }}>Impedance · 8ch</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--warning)" }}>Cz loose</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 4 }}>
          {CHANNELS.map((ch) => (
            <div key={ch.name} style={{
              padding: "4px 0",
              background: ch.ok ? "rgba(94, 116, 38, 0.18)" : "rgba(168, 51, 35, 0.35)",
              border: `1px solid ${ch.ok ? "#3a4f1c" : "var(--danger)"}`,
              borderRadius: 2,
              textAlign: "center",
            }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: ch.ok ? "var(--paper-deep)" : "var(--paper)" }}>{ch.name}</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 10, fontWeight: 600, color: ch.ok ? "var(--paper)" : "var(--paper)" }}>{ch.kΩ.toFixed(1)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DarkMetric({ label, sub, value, unit, trend, data }: {
  label: string; sub: string; value: string; unit: string; trend: string; data: number[];
}) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "70px 1fr 70px", gap: "var(--s-3)",
      alignItems: "center", paddingBottom: 6, borderBottom: "1px solid #2c2620",
    }}>
      <div>
        <div style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", fontWeight: 500, color: "var(--paper)" }}>{label}</div>
        <div style={{ fontSize: 10, color: "var(--paper-deep)", fontFamily: "var(--mono)" }}>{sub}</div>
      </div>
      <DarkSparkline data={data} />
      <div style={{ textAlign: "right", fontFamily: "var(--mono)" }}>
        <span style={{ fontSize: "var(--t-sm)", fontWeight: 600, color: "var(--paper)" }}>{value}</span>
        <span style={{ fontSize: "var(--t-xs)", color: "var(--paper-deep)", marginLeft: 2 }}>{unit}</span>
        <span style={{ fontSize: "var(--t-xs)", color: "var(--olive-soft)", marginLeft: 4 }}>{trend}</span>
      </div>
    </div>
  );
}

function DarkSparkline({ data }: { data: number[] }) {
  const W = 200, H = 18;
  const max = Math.max(...data), min = Math.min(...data);
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - ((v - min) / (max - min || 1)) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: H }}>
      <polyline points={points} fill="none" stroke="#a89776" strokeWidth={1} strokeLinejoin="round" strokeLinecap="round" opacity={0.85} />
    </svg>
  );
}

const BEHAVIOR_MARKERS = [
  { t: "+02:14", kind: "blink",   tone: "petrol",  note: "頻繁眨眼", alpha: "10.2" },
  { t: "+05:47", kind: "fidget",  tone: "warning", note: "椅上扭動 · 可能疲勞", alpha: "8.4" },
  { t: "+08:02", kind: "engaged", tone: "olive",   note: "明顯專注，肩膀放鬆", alpha: "12.6" },
  { t: "+10:31", kind: "yawn",    tone: "warning", note: "打哈欠 · 考慮休息", alpha: "11.0" },
];

/* ─── TAB 2: 訊號 — 完整阻抗 + 4ch raw EEG preview ─── */
function SignalsTab() {
  return (
    <div className="col" style={{ flex: 1, gap: "var(--s-4)", minHeight: 0 }}>
      <div className="surface" style={{ padding: "var(--s-4) var(--s-5)" }}>
        <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
          <div>
            <div className="serial">Impedance · 8ch · target &lt; 10 kΩ</div>
            <div style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)", marginTop: 4 }}>
              7 / 8 channels good · <span style={{ color: "var(--danger)" }}>Cz loose · 重壓電極</span>
            </div>
          </div>
          <span className="chip chip--warning">1 issue</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: "var(--s-2)" }}>
          {CHANNELS.map((ch) => <ImpedanceGauge key={ch.name} {...ch} />)}
        </div>
      </div>

      <div className="surface" style={{ padding: "var(--s-4) var(--s-5)", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
          <div className="serial">Raw EEG preview · 100 SPS · 4 channels</div>
          <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink-faint)" }}>
            ±50 µV · last 3.2s · 1000 SPS authoritative on case
          </span>
        </div>
        <div className="col" style={{ gap: "var(--s-2)", flex: 1, minHeight: 0 }}>
          {(["Fp1", "Fp2", "C3", "Pz"] as const).map((ch, i) => (
            <RawEegChannel key={ch} name={ch} seed={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RawEegChannel({ name, seed }: { name: string; seed: number }) {
  const samples = 360;
  const data = Array.from({ length: samples }, (_, i) => {
    const t = i / samples;
    return Math.sin(t * (60 + seed * 20)) * 0.4 + Math.sin(t * (200 + seed * 30)) * 0.18 +
           (Math.random() - 0.5) * 0.1 + (i === (160 + seed * 12) ? -0.5 : 0);
  });
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "60px 1fr 70px", alignItems: "center", gap: "var(--s-3)",
      paddingBottom: "var(--s-2)", borderBottom: "1px solid var(--hairline-soft)", flex: 1, minHeight: 0,
    }}>
      <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-sm)", fontWeight: 600 }}>{name}</span>
      <div style={{ flex: 1, height: "100%", minHeight: 36 }}><Sparkline data={data} /></div>
      <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink-soft)", textAlign: "right" }}>
        {(Math.abs(data[data.length - 1]) * 50).toFixed(1)} µV
      </span>
    </div>
  );
}

/* ─── TAB 3: 紀錄 ─── */
function LogTab() {
  return (
    <div className="surface" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, padding: "var(--s-5)" }}>
      <div className="row row--between" style={{ marginBottom: "var(--s-4)" }}>
        <div>
          <div className="serial">Session log · 即時事件</div>
          <div style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)" }}>
            13 events · session.start ~ 11m 47s ago · auto-saved D1
          </div>
        </div>
        <button className="btn btn--ghost btn--small">匯出 CSV</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--t-sm)" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--ink)" }}>
              <th style={{ textAlign: "left", padding: "8px 12px 8px 0" }}><span className="serial">Time</span></th>
              <th style={{ textAlign: "left", padding: "8px 12px" }}><span className="serial">Actor</span></th>
              <th style={{ textAlign: "left", padding: "8px 12px" }}><span className="serial">Event</span></th>
              <th style={{ textAlign: "left", padding: "8px 0 8px 12px" }}><span className="serial">Detail</span></th>
            </tr>
          </thead>
          <tbody>
            {EXTENDED_LOG.map((row, i) => (
              <tr key={i} style={{ borderBottom: "1px solid var(--hairline-soft)" }}>
                <td style={{ padding: "10px 12px 10px 0", fontFamily: "var(--mono)", color: "var(--ink-soft)" }}>{row.t}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span className="chip" style={{
                    background: row.who === "yui"     ? "var(--petrol-soft)"  :
                                row.who === "trainer" ? "var(--rust-soft)"    :
                                row.who === "case"    ? "var(--olive-soft)"   :
                                                        "var(--paper-deep)",
                    color:      row.who === "yui"     ? "var(--petrol)"       :
                                row.who === "trainer" ? "var(--rust-deep)"    :
                                row.who === "case"    ? "var(--olive)"        :
                                                        "var(--ink-soft)",
                  }}>{row.who}</span>
                </td>
                <td style={{ padding: "10px 12px", fontFamily: "var(--mono)" }}>{row.ev}</td>
                <td style={{ padding: "10px 0 10px 12px", color: "var(--ink-soft)" }}>{row.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Right rail · pure parameters + apps · designed to fit no-scroll ─── */
function RightRail() {
  return (
    <>
      {/* Protocol presets */}
      <div>
        <div className="row row--between" style={{ marginBottom: 6 }}>
          <div className="serial">Protocol presets</div>
          <button style={{
            background: "transparent", border: "none", cursor: "pointer",
            fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--rust)",
          }}>＋ save current</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          <PresetTile name="alpha-uplift · 注意力" active />
          <PresetTile name="theta-beta · 抑制" />
          <PresetTile name="HRV · 呼吸調節" />
          <PresetTile name="custom · 林氏" />
        </div>
      </div>

      {/* Source / Game */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-3)" }}>
        <div>
          <div className="serial" style={{ marginBottom: 4 }}>Source</div>
          <Selector value="EEG · sgimacog" alt="1000 SPS · 8ch" />
        </div>
        <div>
          <div className="serial" style={{ marginBottom: 4 }}>Game</div>
          <Selector value="SoraMynd plane" alt="alpha · altitude" />
        </div>
      </div>

      {/* Reward formula compact editor */}
      <div>
        <div className="row row--between" style={{ marginBottom: 4 }}>
          <div className="serial">Reward formula</div>
          <span className="serial" style={{ color: "var(--olive)" }}>● ok</span>
        </div>
        <pre style={{
          padding: "10px 12px",
          background: "#1c1814", color: "#f7f3ed",
          borderRadius: "var(--r-sm)",
          fontFamily: "var(--mono)", fontSize: "var(--t-xs)", lineHeight: 1.55,
          margin: 0, whiteSpace: "pre-wrap",
        }}>
          <span style={{ color: "#a89776" }}>// reward ∈ [0, 1]</span>{"\n"}
          <span style={{ color: "#e0a065" }}>let</span> <span style={{ color: "#c4471f" }}>r</span> = (alpha &gt; <span style={{ color: "#e0a065" }}>T_a</span>) ? <span style={{ color: "#e0a065" }}>1</span> : <span style={{ color: "#e0a065" }}>0</span>;{"\n"}
          <span style={{ color: "#e0a065" }}>if</span> (impedance.Cz &gt; <span style={{ color: "#e0a065" }}>15</span>) <span style={{ color: "#c4471f" }}>r</span> = <span style={{ color: "#e0a065" }}>0</span>;{"\n"}
          <span style={{ color: "#e0a065" }}>return</span> <span style={{ color: "#c4471f" }}>r</span>;
        </pre>
      </div>

      {/* Thresholds — 2-col grid */}
      <div>
        <div className="serial" style={{ marginBottom: 6 }}>Thresholds</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-3)" }}>
          <Slider label="T_a · alpha µV²"  value={12.0} min={0} max={30}  display="12.0" />
          <Slider label="T_b · θ/β"        value={2.5}  min={0} max={5}   display="2.5" />
          <Slider label="T_h · rmssd ms"   value={40}   min={0} max={120} display="40" />
          <Slider label="ε · smoothing"    value={0.30} min={0} max={1}   display="0.30" />
        </div>
      </div>

      {/* Action */}
      <div>
        <div className="serial" style={{ marginBottom: 6 }}>Action</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 6 }}>
          <button className="btn btn--ghost btn--small" disabled style={{ justifyContent: "center" }}>▶ Running</button>
          <button className="btn btn--ghost btn--small" style={{ justifyContent: "center" }}>⏸ Pause</button>
          <button className="btn btn--small" style={{
            background: "var(--paper-warm)", color: "var(--danger)",
            borderColor: "var(--danger)", justifyContent: "center",
          }}>✋ Stop &amp; save</button>
        </div>
      </div>

      {/* Apps quick-launch — push to bottom */}
      <div style={{ marginTop: "auto" }}>
        <div className="serial" style={{ marginBottom: 6 }}>Apps · case browser</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
          <AppLink name="SoraMynd"  url="/soramynd"  active />
          <AppLink name="VisioMynd" url="/visiomynd" />
          <AppLink name="MyndScape" url="/myndscape" />
          <AppLink name="sgimacog"  url="/eeg" hint="baseline" />
        </div>
      </div>
    </>
  );
}

function PresetTile({ name, active }: { name: string; active?: boolean }) {
  return (
    <button style={{
      padding: "10px 12px",
      border: `1px solid ${active ? "var(--ink)" : "var(--hairline)"}`,
      background: active ? "var(--paper)" : "transparent",
      borderRadius: "var(--r-sm)",
      fontSize: "var(--t-xs)",
      textAlign: "left",
      cursor: "pointer",
      fontFamily: "var(--body)",
      color: active ? "var(--ink)" : "var(--ink-soft)",
      boxShadow: active ? "2px 2px 0 var(--rust)" : "none",
      transition: "all 160ms",
    }}>
      <span className="italic-display" style={{ display: "block", fontSize: "var(--t-sm)", marginBottom: 2 }}>
        {name.split(" · ")[0]}
      </span>
      <span style={{ fontFamily: "var(--mono)", color: "var(--ink-faint)", fontSize: 10 }}>
        {name.split(" · ")[1]}
      </span>
    </button>
  );
}

/* ─── data ─── */
const CHANNELS = [
  { name: "Fp1", kΩ: 4.2, ok: true  },
  { name: "Fp2", kΩ: 5.1, ok: true  },
  { name: "F3",  kΩ: 6.8, ok: true  },
  { name: "F4",  kΩ: 7.4, ok: true  },
  { name: "C3",  kΩ: 5.5, ok: true  },
  { name: "Cz",  kΩ: 18.6, ok: false },
  { name: "C4",  kΩ: 8.9, ok: true  },
  { name: "Pz",  kΩ: 6.2, ok: true  },
];

const EXTENDED_LOG = [
  { t: "19:00:12", who: "system",  ev: "session.start",      detail: "DO room created · trainer + case both present" },
  { t: "19:00:35", who: "yui",     ev: "advisory",           detail: "Cz 阻抗偏高警示" },
  { t: "19:00:48", who: "case",    ev: "consent.recording",  detail: "consent_logs id=1 inserted" },
  { t: "19:00:52", who: "trainer", ev: "consent.recording",  detail: "consent_logs id=2 inserted" },
  { t: "19:01:05", who: "case",    ev: "consent.no_medical", detail: "consent_logs id=3 inserted" },
  { t: "19:01:12", who: "trainer", ev: "set_source: eeg",    detail: "" },
  { t: "19:01:22", who: "trainer", ev: "set_game: plane",    detail: "" },
  { t: "19:01:28", who: "trainer", ev: "start_session",      detail: "all consents satisfied · phase → live" },
  { t: "19:03:14", who: "trainer", ev: "param_update",       detail: "threshold 0.55 → 0.65" },
  { t: "19:08:02", who: "yui",     ev: "advisory",           detail: "alpha sustained > baseline ×1.4 for 60s" },
  { t: "19:11:47", who: "trainer", ev: "param_update",       detail: "threshold 0.65 → 0.72" },
  { t: "19:11:55", who: "yui",     ev: "advisory",           detail: "建議 8-10 分後考慮升 0.78 或暫停" },
];

const YUI_MESSAGES: { from: "yui" | "trainer"; text: string; t: string }[] = [
  { from: "yui",     text: "Cz 阻抗偏高 (18.6 kΩ)，建議重壓電極或先跳過此 channel。", t: "19:00:35" },
  { from: "trainer", text: "先試壓緊看看。", t: "19:00:52" },
  { from: "yui",     text: "個案 alpha 已上升至 12.4 µV²，超 baseline 1.4×。reward rate 良好。", t: "19:08:10" },
  { from: "yui",     text: "建議 8-10 分後可考慮提高 threshold（目前 0.72）至 0.78 拉強度，或暫停讓個案休息。", t: "19:11:55" },
];

/* ─── components ─── */
function VideoTile({ kind, label, caption }: { kind: "webcam" | "game"; label: string; caption: string }) {
  const ratio = kind === "webcam" ? "4 / 3" : "16 / 9";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>
      <div className="serial" style={{ display: "flex", justifyContent: "space-between" }}>
        <span>{label}</span>
        <span style={{ color: "var(--olive)" }}>● live</span>
      </div>
      <div style={{
        flex: 1,
        aspectRatio: ratio,
        borderRadius: "var(--r-md)",
        position: "relative",
        overflow: "hidden",
        border: "1px solid var(--ink)",
        background: kind === "webcam"
          ? "radial-gradient(ellipse at center, #57514a 0%, #1c1814 80%)"
          : "linear-gradient(135deg, #20534b 0%, #1c1814 50%, #c4471f33 100%)",
        boxShadow: "4px 4px 0 var(--ink)",
        maxHeight: "100%",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(255,255,255,0.018) 2px, rgba(255,255,255,0.018) 3px)`,
          mixBlendMode: "overlay",
        }} />
        {kind === "webcam" && (
          <svg viewBox="0 0 100 75" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <ellipse cx="50" cy="34" rx="11" ry="13" fill="#d6c8ad" opacity="0.55" />
            <path d="M28,75 C28,55 38,48 50,48 C62,48 72,55 72,75 Z" fill="#a89776" opacity="0.55" />
          </svg>
        )}
        {kind === "game" && (
          <svg viewBox="0 0 320 180" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <defs>
              <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#f7d8b4" />
                <stop offset="60%"  stopColor="#e9b489" />
                <stop offset="100%" stopColor="#a36a45" />
              </linearGradient>
            </defs>
            <rect width="320" height="180" fill="url(#sky)" />
            <circle cx="240" cy="60" r="22" fill="#fbf6e6" opacity="0.85" />
            <g transform="translate(110,90) rotate(-4)" fill="#1c1814">
              <path d="M0,0 L40,0 L52,5 L40,10 L0,10 Z" />
              <path d="M14,-12 L26,-12 L22,0 L18,0 Z" />
              <path d="M14,10 L26,10 L22,22 L18,22 Z" />
            </g>
            <ellipse cx="60"  cy="40"  rx="22" ry="6" fill="white" opacity="0.5" />
            <ellipse cx="200" cy="130" rx="34" ry="7" fill="white" opacity="0.4" />
          </svg>
        )}
        <div style={{
          position: "absolute", top: 8, left: 8,
          background: "rgba(28,24,20,0.7)", color: "var(--paper)",
          fontFamily: "var(--mono)", fontSize: 10, padding: "2px 6px", letterSpacing: "0.05em",
        }}>
          {kind === "webcam" ? "CASE-CAM" : "CASE-SCREEN"}
        </div>
      </div>
      <div className="muted" style={{ fontSize: "var(--t-xs)", fontFamily: "var(--mono)" }}>{caption}</div>
    </div>
  );
}

function ImpedanceGauge({ name, kΩ, ok }: { name: string; kΩ: number; ok: boolean }) {
  const pct = Math.min(kΩ / 25, 1);
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
      padding: "8px 4px",
      background: ok ? "var(--paper)" : "var(--danger-soft)",
      border: `1px solid ${ok ? "var(--hairline)" : "var(--danger)"}`,
      borderRadius: "var(--r-sm)",
    }}>
      <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--ink-soft)" }}>{name}</span>
      <div style={{ position: "relative", width: 6, height: 28, background: "var(--paper-deep)", borderRadius: 1 }}>
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          height: `${pct * 100}%`,
          background: ok ? "var(--olive)" : "var(--danger)",
        }} />
      </div>
      <span style={{ fontFamily: "var(--mono)", fontSize: 10, fontWeight: 500, color: ok ? "var(--ink)" : "var(--danger)" }}>
        {kΩ.toFixed(1)}
      </span>
    </div>
  );
}

function MetricRow({ label, sub, value, unit, trend, data, primary }: {
  label: string; sub: string; value: string; unit: string;
  trend: "rising" | "steady" | "falling"; data: number[]; primary?: boolean;
}) {
  const arrow = trend === "rising" ? "↗" : trend === "falling" ? "↘" : "→";
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "100px 1fr 90px",
      gap: "var(--s-4)", alignItems: "center",
      padding: primary ? "8px 0" : "4px 0",
      borderBottom: "1px solid var(--hairline-soft)",
    }}>
      <div>
        <div style={{
          fontFamily: "var(--mono)",
          fontSize: primary ? "var(--t-sm)" : "var(--t-xs)",
          fontWeight: primary ? 600 : 500,
          color: primary ? "var(--rust)" : "var(--ink)",
          letterSpacing: "0.04em",
        }}>{label}</div>
        <div className="muted" style={{ fontSize: "var(--t-xs)" }}>{sub}</div>
      </div>
      <Sparkline data={data} primary={primary} />
      <div style={{ textAlign: "right", fontFamily: "var(--mono)" }}>
        <span style={{ fontSize: primary ? "var(--t-md)" : "var(--t-sm)", fontWeight: 600 }}>{value}</span>
        <span style={{ fontSize: "var(--t-xs)", color: "var(--ink-faint)", marginLeft: 4 }}>{unit}</span>
        <span style={{ fontSize: "var(--t-xs)", color: trend === "rising" ? "var(--olive)" : "var(--ink-faint)", marginLeft: 6 }}>{arrow}</span>
      </div>
    </div>
  );
}

function Sparkline({ data, primary }: { data: number[]; primary?: boolean }) {
  const W = 280, H = 24;
  const max = Math.max(...data), min = Math.min(...data);
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - ((v - min) / (max - min || 1)) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: H }}>
      <polyline
        points={points}
        fill="none"
        stroke={primary ? "var(--rust)" : "var(--ink)"}
        strokeWidth={primary ? 1.5 : 1}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={primary ? 1 : 0.7}
      />
    </svg>
  );
}

function RawEegStrip() {
  const samples = 320;
  const data = Array.from({ length: samples }, (_, i) => {
    const t = i / samples;
    return Math.sin(t * 80) * 0.4 + Math.sin(t * 220) * 0.18 +
           (Math.random() - 0.5) * 0.12 + (i === 200 ? -0.6 : 0);
  });
  return (
    <div style={{ paddingTop: "var(--s-2)", marginTop: "auto" }}>
      <div className="row row--between" style={{ marginBottom: 4 }}>
        <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink)" }}>
          raw · Fp1 · 100 SPS preview
        </span>
        <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink-faint)" }}>
          ±50 µV scale · last 3.2s
        </span>
      </div>
      <Sparkline data={data} />
    </div>
  );
}

function YuiBubble({ from, text, t }: { from: "yui" | "trainer"; text: string; t: string }) {
  const isYui = from === "yui";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: "var(--s-3)" }}>
      <div className="serial" style={{
        color: isYui ? "var(--petrol)" : "var(--rust)",
        textAlign: "left", paddingTop: 2,
      }}>
        {isYui ? "Yui" : "You"}
        <br />
        <span style={{ color: "var(--ink-ghost)" }}>{t.slice(0, 5)}</span>
      </div>
      <div style={{
        background: isYui ? "var(--petrol-soft)" : "var(--paper)",
        borderLeft: `2px solid ${isYui ? "var(--petrol)" : "var(--rust)"}`,
        padding: "10px 14px",
        borderRadius: "0 var(--r-sm) var(--r-sm) 0",
        fontSize: "var(--t-sm)",
        lineHeight: 1.55,
      }}>
        {text}
      </div>
    </div>
  );
}

function Stat({ label, value, unit, trend, small, primary, warn }: {
  label: string; value: string; unit?: string; trend?: string;
  small?: string; primary?: boolean; warn?: boolean;
}) {
  return (
    <div style={{
      borderLeft: primary ? "2px solid var(--rust)" : warn ? "2px solid var(--warning)" : "2px solid var(--hairline)",
      paddingLeft: 10,
    }}>
      <div className="serial" style={{
        color: primary ? "var(--rust)" : warn ? "var(--warning)" : "var(--ink-faint)",
      }}>{label}</div>
      <div style={{
        fontFamily: "var(--mono)",
        fontSize: primary ? "var(--t-md)" : "var(--t-base)",
        fontWeight: 600,
        color: primary ? "var(--rust)" : warn ? "var(--warning)" : "var(--ink)",
        marginTop: 2,
      }}>
        {value}
        {unit && <span style={{ fontSize: "var(--t-xs)", color: "var(--ink-faint)", marginLeft: 3 }}>{unit}</span>}
        {trend && <span style={{ fontSize: "var(--t-xs)", color: "var(--olive)", marginLeft: 4 }}>{trend}</span>}
      </div>
      {small && <div className="muted" style={{ fontSize: "var(--t-xs)" }}>{small}</div>}
    </div>
  );
}

function Selector({ value, alt }: { value: string; alt: string }) {
  return (
    <button style={{
      width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start",
      padding: "10px 14px", background: "var(--surface)", border: "1px solid var(--hairline)",
      borderRadius: "var(--r-sm)", cursor: "pointer", textAlign: "left",
      transition: "border-color 160ms",
    }}>
      <span style={{ fontFamily: "var(--display)", fontSize: "var(--t-base)", fontWeight: 500 }}>
        {value} <span className="muted" style={{ fontSize: "var(--t-xs)" }}>▾</span>
      </span>
      <span className="muted" style={{ fontSize: "var(--t-xs)", marginTop: 2, fontFamily: "var(--mono)" }}>{alt}</span>
    </button>
  );
}

function Slider({ label, value, min, max, display }: { label: string; value: number; min: number; max: number; display?: string }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="row row--between" style={{ marginBottom: 6, fontSize: "var(--t-xs)" }}>
        <span className="muted" style={{ fontFamily: "var(--mono)" }}>{label}</span>
        <span style={{ fontFamily: "var(--mono)", fontWeight: 600 }}>{display ?? value.toFixed(2)}</span>
      </div>
      <div style={{ position: "relative", height: 14 }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: 6, height: 2, background: "var(--hairline)" }} />
        <div style={{ position: "absolute", left: 0, top: 6, height: 2, width: `${pct}%`, background: "var(--rust)" }} />
        <div style={{
          position: "absolute", left: `calc(${pct}% - 7px)`, top: 0,
          width: 14, height: 14, borderRadius: "50%",
          background: "var(--paper)", border: "1.5px solid var(--rust)", cursor: "grab",
        }} />
      </div>
    </div>
  );
}

function AppLink({ name, url, active, hint }: { name: string; url: string; active?: boolean; hint?: string }) {
  return (
    <a href={url} style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "8px 10px", border: `1px solid ${active ? "var(--ink)" : "var(--hairline)"}`,
      background: active ? "var(--ink)" : "transparent", color: active ? "var(--paper)" : "var(--ink)",
      fontSize: "var(--t-sm)", borderRadius: "var(--r-sm)",
    }}>
      <span className="italic-display" style={{ fontWeight: active ? 600 : 400, fontStyle: "italic" }}>{name}</span>
      <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", opacity: 0.7 }}>
        {hint ? hint : url} ↗
      </span>
    </a>
  );
}

function generateSeries(n: number, trend: number): number[] {
  let v = 0.3 + Math.random() * 0.2;
  return Array.from({ length: n }, () => {
    v += (Math.random() - 0.5 + trend * 0.05) * 0.04;
    v = Math.max(0, Math.min(1, v));
    return v;
  });
}

/* ═══════════════════════════════════════════════════════════════════
   DASHBOARD — same as before
   ═══════════════════════════════════════════════════════════════════ */
const cases = [
  { id: "01KR2DX8", name: "張小明", age: "6 歲 4 月",   sessions: 12, last: "2026-05-02", status: "active"     },
  { id: "01KR2DY4", name: "Lin Y.", age: "8 歲 11 月", sessions: 7,  last: "2026-04-29", status: "active"     },
  { id: "01KR2DZ1", name: "陳宇承",  age: "5 歲 2 月",   sessions: 3,  last: "2026-04-22", status: "intake"     },
  { id: "01KR2E0K", name: "M. Park", age: "10 歲 0 月",  sessions: 18, last: "2026-04-30", status: "completed"  },
];
const upcoming = [
  { id: "S001", who: "張小明",    when: "Today · 19:00", proto: "EEG · alpha-uplift" },
  { id: "S002", who: "Lin Y.",    when: "Tomorrow · 16:30", proto: "HRV · resonance" },
  { id: "S003", who: "陳宇承",    when: "Fri · 10:00", proto: "MyndScape · n-back" },
];

function DashboardDemo() {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <header className="mast">
        <div className="mast__brand">
          <span>CogniHome</span>
          <span className="slash">/</span>
          <span style={{ color: "var(--ink-soft)" }}>Trainer Dashboard</span>
        </div>
        <nav className="mast__nav">
          <a href="#" className="active">Cases</a>
          <a href="#">Sessions</a>
          <a href="#">Notes</a>
          <span style={{ color: "var(--ink-ghost)" }}>·</span>
          <a href="#">Sign out</a>
        </nav>
      </header>

      <main className="page">
        <section style={{ marginBottom: "var(--s-9)" }}>
          <div className="serial rise rise-1" style={{ marginBottom: "var(--s-3)" }}>
            VOL 01 · ISSUE 08 · MAY 2026
          </div>
          <h1 className="rise rise-2" style={{ maxWidth: 760 }}>
            訓練師工作台 — <span className="italic-display" style={{ color: "var(--rust)" }}>個案 ＆ 會次</span> 的安靜中樞。
          </h1>
          <p className="rise rise-3" style={{ fontSize: "var(--t-md)", color: "var(--ink-soft)", marginTop: "var(--s-4)", maxWidth: 580, lineHeight: 1.6 }}>
            建立個案、排訓練、寫筆記。在開始 session 之前你看見的是視訊，
            開始之後 — 螢幕變成遊戲、攝影機留在角落、訊號在中央。
          </p>
          <div className="row rise rise-4" style={{ marginTop: "var(--s-6)", gap: "var(--s-3)" }}>
            <button className="btn btn--rust" onClick={() => setShowModal(true)}>＋ 建立個案</button>
            <button className="btn btn--ghost">排定新 session</button>
          </div>
        </section>

        <section className="rise rise-5" style={{ marginBottom: "var(--s-8)" }}>
          <div className="section-head">
            <div>
              <div className="eyebrow">Upcoming</div>
              <h2>近期 sessions</h2>
            </div>
            <a href="#" style={{ fontSize: "var(--t-sm)" }}>看全部 →</a>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "var(--s-4)" }}>
            {upcoming.map((u) => (
              <article key={u.id} className="surface" style={{ padding: "var(--s-5)" }}>
                <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
                  <span className="serial">{u.id}</span>
                  <span className="chip chip--rust">scheduled</span>
                </div>
                <div className="italic-display" style={{ fontSize: "var(--t-xl)", fontWeight: 400, marginBottom: "var(--s-2)" }}>{u.who}</div>
                <div className="muted" style={{ fontSize: "var(--t-sm)", marginBottom: "var(--s-3)" }}>{u.when}</div>
                <div className="kv">
                  <span className="kv__key">Protocol</span>
                  <span className="kv__val tabular">{u.proto}</span>
                </div>
                <div className="row" style={{ marginTop: "var(--s-5)" }}>
                  <button className="btn btn--small">複製連結</button>
                  <button className="btn btn--small btn--ghost">編輯</button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="rise rise-6">
          <div className="section-head">
            <div>
              <div className="eyebrow">Roster · {cases.length} 位</div>
              <h2>我的個案</h2>
            </div>
            <input type="text" placeholder="搜尋個案名 / ID …" style={{ maxWidth: 280, fontSize: "var(--t-sm)" }} />
          </div>
          <div className="list">
            <div className="list-row" style={{ borderBottom: "1px solid var(--ink)", paddingBottom: "var(--s-2)", marginBottom: "var(--s-1)" }}>
              <div className="serial">Name</div>
              <div className="serial">Age</div>
              <div className="serial">Last session</div>
              <div className="serial" style={{ textAlign: "right" }}>Status</div>
            </div>
            {cases.map((cs) => (
              <div key={cs.id} className="list-row">
                <div>
                  <div className="list-row__name">{cs.name}</div>
                  <div className="list-row__meta">{cs.id}</div>
                </div>
                <div className="muted tabular">{cs.age}</div>
                <div>
                  <div className="tabular">{cs.last}</div>
                  <div className="list-row__meta">{cs.sessions} sessions</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {cs.status === "active"    && <span className="chip chip--olive">active</span>}
                  {cs.status === "intake"    && <span className="chip chip--petrol">intake</span>}
                  {cs.status === "completed" && <span className="chip">completed</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="serial" style={{ marginBottom: "var(--s-2)" }}>New case</div>
            <h3 className="modal__title">建立新個案</h3>
            <div className="modal__sub">基本資料先填，session 排程下一步做。</div>
            <div className="col" style={{ gap: "var(--s-4)" }}>
              <div className="field"><label className="field-label">姓名 / 暱稱</label><input type="text" placeholder="例：張小明" autoFocus /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-3)" }}>
                <div className="field"><label className="field-label">出生年月</label><input type="date" /></div>
                <div className="field"><label className="field-label">性別 (選填)</label><select><option>未指定</option><option>男</option><option>女</option><option>其他</option></select></div>
              </div>
              <div className="field"><label className="field-label">備註</label><textarea placeholder="主訴、目標、注意事項…" /></div>
            </div>
            <div className="modal__actions">
              <button className="btn btn--ghost" onClick={() => setShowModal(false)}>取消</button>
              <button className="btn btn--rust" onClick={() => setShowModal(false)}>建立</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
